import {z} from "zod";
import {IANAZone} from "luxon";
import {getDefaultCountry} from "@/utils/locales";
import countries, { Country } from "world-countries";

export const i18n = {
    defaultLocale: "en",
    defaultCountry: "US",
    defaultTimezone: "Europe/Berlin",
    locales: ["en", "de", "fr", "es", "pt"],
    localesFullAlias: {
        en: "en-US", // English (United States)
        de: "de-DE", // German (Germany)
        fr: "fr-FR", // French (France)
        es: "es-ES", // Spanish (Spain)
        pt: "pt-PT", // Portuguese (Portugal)
    },
} as const;

export const LocaleToISO6393: Record<string, string> = {
    en: "eng",
    de: "deu",
    fr: "fra",
    es: "spa",
    pt: "por",
};

export type Locale = (typeof i18n)["locales"][number];

/**
 * Lookup table for countries by their ISO 3166-1 alpha-2 (CCA2) code.
 */
const countriesByCCA2 = new Map<string, Country>(
    countries.map(c => [c.cca2, c])
);

/**
 * Simple in-memory cache for resolved country names.
 * Key format: `${cca2}|${locale}` → localized name string.
 */
const countryNameCache = new Map<string, string>();

/**
 * Resolve the display name of a country in a given locale.
 *
 * Resolution order:
 *  1. Localized translation (if available for the locale, using ISO 639-3).
 *  2. English translation (if available).
 *  3. Fallback to the country’s `name.common` property.
 *  4. Empty string if nothing else found.
 *
 * Results are cached per (country, locale) combination.
 *
 * @param country - The `Country` object (from `world-countries`).
 * @param locale - Optional BCP-47 locale string (e.g. "pt-BR", "de").
 * @returns The best available country name for the locale.
 */
const resolveCountryName = (country: Country, locale?: string): string => {

    const cacheKey = `${country.cca2}|${locale ?? ""}`;
    const cached = countryNameCache.get(cacheKey);

    if (cached) return cached;

    const translationKey = locale && (LocaleToISO6393[locale] || LocaleToISO6393[locale.split("-")[0]]);

    const translations = (country.translations as any) || {};
    const localized = translationKey && translations[translationKey]?.common;
    const english = translations.eng?.common;
    const fallback = country.name?.common;

    const result = localized || english || fallback || "";

    countryNameCache.set(cacheKey, result);

    return result;

};

/**
 * Get the localized display name of a country from a `Country` object.
 *
 * @param locale - BCP-47 locale string (e.g. "en", "pt-BR").
 * @param country - A `Country` object (from `world-countries`).
 * @returns The localized country name, or an empty string if not resolvable.
 */
export const getCountryName = (locale: Locale, country: Country): string =>
    country ? resolveCountryName(country, locale) : "";

/**
 * Get the localized display name of a country from its ISO 3166-1 alpha-2 code.
 *
 * @param locale - BCP-47 locale string (e.g. "en", "pt-BR").
 * @param code - ISO 3166-1 alpha-2 code (e.g. "DE", "US").
 * @returns The localized country name, or an empty string if not resolvable or invalid code.
 */
export const getCountryNameByCode = (locale: Locale, code: string): string => {
    if (!code) return "";
    const country = countriesByCCA2.get(code.toUpperCase());
    return country ? resolveCountryName(country, locale) : "";
};

export const validateCountry = (countryCode: any) => {
    const countryCodes = countries.map(country => country.cca2);
    return (countryCodes.includes(String(countryCode))) ? countryCode : getDefaultCountry();
}

export const validateLang = (lang: any): Locale => {
    return i18n.locales.includes(lang) ? (lang as Locale) : i18n.defaultLocale;
}

export const validateTimezone = (timezone: string): string => {
    return IANAZone.isValidZone(String(timezone)) ? timezone : i18n.defaultTimezone;
}

export const zLang  = (message = "Invalid locale provided") => z.any().refine((lang) => i18n.locales.includes(lang), {
        message: "Invalid language",
    }
);
